"use client";

import { type UseContactSalesFormV2Return } from "../hooks/useContactSalesFormV2";
import { isPersonalEmail, buildFieldOptions, isExpectedMonthlyUsageCountry } from "../shared/utils";
import { companySizeOptions, fieldConfig } from "../shared/constants";

type Step2Props = Pick<UseContactSalesFormV2Return, "form" | "validators" | "t">;

export function Step2({ form, validators, t }: Step2Props) {
  return (
    <>
      <div className='tw-flex tw-gap-4'>
        <div className='tw-flex-1 tw-min-w-0'>
          <form.AppField
            name={fieldConfig.first_name.name}
            validators={{
              onMount: validators.first_name,
              onChange: validators.first_name,
            }}
          >
            {field => (
              <field.Input
                name={fieldConfig.first_name.name}
                label={t("fields.first_name.label")}
                placeholder={t("fields.first_name.placeholder")}
                required={fieldConfig.first_name.required}
              />
            )}
          </form.AppField>
        </div>
        <div className='tw-flex-1 tw-min-w-0'>
          <form.AppField
            name={fieldConfig.last_name.name}
            validators={{
              onMount: validators.last_name,
              onChange: validators.last_name,
            }}
          >
            {field => (
              <field.Input
                name={fieldConfig.last_name.name}
                label={t("fields.last_name.label")}
                placeholder={t("fields.last_name.placeholder")}
                required={fieldConfig.last_name.required}
              />
            )}
          </form.AppField>
        </div>
      </div>
      <form.Subscribe selector={state => state.values.email}>
        {email =>
          isPersonalEmail(email) && (
            <>
              <form.AppField
                name={fieldConfig.company.name}
                validators={{
                  onMount: validators.company,
                  onChange: validators.company,
                }}
              >
                {field => (
                  <field.Input
                    name={fieldConfig.company.name}
                    label={t("fields.company.label")}
                    placeholder={t("fields.company.placeholder")}
                    required={fieldConfig.company.required}
                  />
                )}
              </form.AppField>
              <div className='tw-flex tw-flex-col md:tw-flex-row md:tw-gap-4'>
                <div className='tw-flex-1 md:tw-min-w-0'>
                  <form.AppField
                    name={fieldConfig.industry.name}
                    validators={{
                      onMount: validators.industry,
                      onChange: validators.industry,
                    }}
                  >
                    {field => (
                      <field.Select
                        name={fieldConfig.industry.name}
                        label={t("fields.industry.label")}
                        unselectedLabel={t("fields.industry.unselectedLabel")}
                        options={buildFieldOptions("industry", t)}
                        required={fieldConfig.industry.required}
                      />
                    )}
                  </form.AppField>
                </div>
                <div className='tw-flex-1 md:tw-min-w-0'>
                  <form.AppField
                    name={fieldConfig.company_size.name}
                    validators={{
                      onMount: validators.company_size,
                      onChange: validators.company_size,
                    }}
                  >
                    {field => (
                      <field.Select
                        name={fieldConfig.company_size.name}
                        label={t("fields.company_size.label")}
                        unselectedLabel={t("fields.company_size.unselectedLabel")}
                        options={companySizeOptions}
                        required={fieldConfig.company_size.required}
                      />
                    )}
                  </form.AppField>
                </div>
              </div>
            </>
          )
        }
      </form.Subscribe>
      <div className='tw-flex xl:tw-gap-4 xl:tw-flex-row tw-flex-col'>
        <div className='tw-flex-1 xl:tw-min-w-0'>
          <form.AppField
            name={fieldConfig.country.name}
            validators={{
              onMount: validators.country,
              onChange: validators.country,
            }}
          >
            {field => (
              <field.CountrySelect
                name={fieldConfig.country.name}
                label={t("fields.country.label")}
                required={fieldConfig.country.required}
              />
            )}
          </form.AppField>
        </div>
        <div className='tw-flex-1 xl:tw-min-w-0'>
          <form.Subscribe selector={state => state.values.country}>
            {country => (
              <form.AppField
                name={fieldConfig.phone_number.name}
                validators={{
                  onChangeListenTo: ["country"],
                  onMount: validators.phone_number,
                  onChange: validators.phone_number,
                }}
              >
                {field => (
                  <field.PhoneInput
                    name={fieldConfig.phone_number.name}
                    label={t("fields.phone_number.label")}
                    placeholder='123 456 7890'
                    required={country !== "United States of America"}
                    country={country}
                  />
                )}
              </form.AppField>
            )}
          </form.Subscribe>
        </div>
      </div>
      <form.Subscribe selector={state => state.values.country}>
        {country =>
          isExpectedMonthlyUsageCountry(country) && (
            <form.AppField
              name={fieldConfig.expected_monthly_usage.name}
              validators={{
                onChangeListenTo: ["country"],
                onMount: validators.expected_monthly_usage,
                onChange: validators.expected_monthly_usage,
              }}
            >
              {field => (
                <field.Select
                  name={fieldConfig.expected_monthly_usage.name}
                  label={t("fields.expected_monthly_usage.label")}
                  unselectedLabel={t("fields.expected_monthly_usage.unselectedLabel")}
                  options={buildFieldOptions("expected_monthly_usage", t)}
                  required={fieldConfig.expected_monthly_usage.required}
                />
              )}
            </form.AppField>
          )
        }
      </form.Subscribe>
      <form.AppField name={fieldConfig.platform_interest.name}>
        {field => (
          <field.Select
            name={fieldConfig.platform_interest.name}
            label={t("fields.platform_interest.label")}
            unselectedLabel={t("fields.platform_interest.unselectedLabel")}
            options={buildFieldOptions("platform_interest", t)}
            required={fieldConfig.platform_interest.required}
          />
        )}
      </form.AppField>
      <form.AppField
        name={fieldConfig.detail.name}
        validators={{
          onMount: validators.detail,
          onChange: validators.detail,
        }}
      >
        {field => (
          <field.Textarea
            name={fieldConfig.detail.name}
            label={t("fields.detail.label")}
            placeholder={t("fields.detail.placeholder")}
            required={fieldConfig.detail.required}
          />
        )}
      </form.AppField>
      <form.AppField
        name={fieldConfig.how_did_you_hear.name}
        validators={{
          onMount: validators.how_did_you_hear,
          onChange: validators.how_did_you_hear,
        }}
      >
        {field => (
          <field.Input
            name={fieldConfig.how_did_you_hear.name}
            label={t("fields.how_did_you_hear.label")}
            placeholder={t("fields.how_did_you_hear.placeholder")}
            required={fieldConfig.how_did_you_hear.required}
          />
        )}
      </form.AppField>
    </>
  );
}
